/**
 * Export Style Configurations
 * Themes and styling options for exports
 */

export interface ExportTheme {
  id: string
  name: string
  description: string
  fonts: {
    title: string
    heading: string
    body: string
    code: string
  }
  colors: {
    primary: string
    secondary: string
    text: string
    textLight: string
    accent: string
  }
  spacing: {
    lineHeight: number
    paragraphSpacing: number
    headingSpacing: number
  }
  pageSize: 'a4' | 'letter' | 'a5'
}

export const EXPORT_THEMES: ExportTheme[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional serif fonts with elegant spacing',
    fonts: {
      title: 'Georgia, serif',
      heading: 'Georgia, serif',
      body: 'Georgia, serif',
      code: 'Courier New, monospace',
    },
    colors: {
      primary: '#1a1a1a',
      secondary: '#2a2a2a',
      text: '#333333',
      textLight: '#666666',
      accent: '#8b4513',
    },
    spacing: {
      lineHeight: 1.8,
      paragraphSpacing: 1.5,
      headingSpacing: 2,
    },
    pageSize: 'a4',
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean sans-serif design with contemporary feel',
    fonts: {
      title: 'Arial, sans-serif',
      heading: 'Arial, sans-serif',
      body: 'Helvetica, Arial, sans-serif',
      code: 'Monaco, monospace',
    },
    colors: {
      primary: '#000000',
      secondary: '#1a1a1a',
      text: '#2c3e50',
      textLight: '#7f8c8d',
      accent: '#3498db',
    },
    spacing: {
      lineHeight: 1.6,
      paragraphSpacing: 1.2,
      headingSpacing: 1.8,
    },
    pageSize: 'letter',
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Sophisticated design with refined typography',
    fonts: {
      title: 'Palatino, serif',
      heading: 'Palatino, serif',
      body: 'Palatino, Georgia, serif',
      code: 'Consolas, monospace',
    },
    colors: {
      primary: '#2c2c2c',
      secondary: '#3d3d3d',
      text: '#4a4a4a',
      textLight: '#888888',
      accent: '#9b59b6',
    },
    spacing: {
      lineHeight: 1.9,
      paragraphSpacing: 1.6,
      headingSpacing: 2.2,
    },
    pageSize: 'a4',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean and simple with maximum readability',
    fonts: {
      title: 'Helvetica, sans-serif',
      heading: 'Helvetica, sans-serif',
      body: 'Helvetica, Arial, sans-serif',
      code: 'Courier, monospace',
    },
    colors: {
      primary: '#000000',
      secondary: '#333333',
      text: '#444444',
      textLight: '#999999',
      accent: '#000000',
    },
    spacing: {
      lineHeight: 1.7,
      paragraphSpacing: 1.4,
      headingSpacing: 2,
    },
    pageSize: 'a5',
  },
  {
    id: 'academic',
    name: 'Academic',
    description: 'Professional format for educational content',
    fonts: {
      title: 'Times New Roman, serif',
      heading: 'Times New Roman, serif',
      body: 'Times New Roman, serif',
      code: 'Courier New, monospace',
    },
    colors: {
      primary: '#000000',
      secondary: '#1a1a1a',
      text: '#2d2d2d',
      textLight: '#666666',
      accent: '#8b0000',
    },
    spacing: {
      lineHeight: 2.0,
      paragraphSpacing: 1.5,
      headingSpacing: 2.5,
    },
    pageSize: 'letter',
  },
]

export interface ExportSettings {
  theme: ExportTheme
  includeCoverPage: boolean
  includeTableOfContents: boolean
  includePageNumbers: boolean
  includeHeaders: boolean
  includeFooters: boolean
  headerText?: string
  footerText?: string
  coverPageImage?: string
}

export const DEFAULT_EXPORT_SETTINGS: ExportSettings = {
  theme: EXPORT_THEMES[0], // Classic
  includeCoverPage: true,
  includeTableOfContents: true,
  includePageNumbers: true,
  includeHeaders: false,
  includeFooters: true,
  footerText: 'Created with Ebook Genius',
}
